













































































































import Vue from 'vue'

interface RubriqueNode {
  id?: string
  label: string
  value?: string
  interencheres?: string
  drouot?: string
  policeBook?: string
  weaponBook?: string
  vehicule?: string
  TVA?: string
  children?: RubriqueNode[]
}

interface TreeNode {
  parent: TreeNode | null
  data: RubriqueNode
  [key: string]: unknown
}

export default Vue.extend({
  name: 'RubriquesTreeEditor',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    showExtraFields: {
      type: Boolean,
      default: true,
    },
    debug: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      treeData: [] as RubriqueNode[],
      defaultProps: {
        children: 'children',
        label: 'label',
      },
      activeCollapse: [] as string[],
      showDebugJson: false,
      editDialogVisible: false,
      editForm: {
        label: '',
        value: '',
        interencheres: '',
        drouot: '',
        policeBook: '',
        weaponBook: '',
        vehicule: '',
        TVA: '',
      },
      currentNode: null as TreeNode | null,
      currentData: null as RubriqueNode | null,
      nodeIdCounter: 0,
      isUpdatingFromProp: false,
      emitTimeout: null as ReturnType<typeof setTimeout> | null,
      isAddingChild: false,
      skipNextPropUpdate: false,
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        // Skip if we just emitted the data back to parent
        if (this.skipNextPropUpdate) {
          this.skipNextPropUpdate = false
          return
        }
        if (newVal && Array.isArray(newVal) && newVal.length > 0) {
          this.isUpdatingFromProp = true
          const copiedData = JSON.parse(JSON.stringify(newVal))
          this.treeData = this.addIdsToNodes(copiedData)
          this.$nextTick(() => {
            this.isUpdatingFromProp = false
          })
        } else if (!newVal || newVal.length === 0) {
          this.treeData = []
        }
      },
    },
  },
  beforeDestroy() {
    if (this.emitTimeout) {
      clearTimeout(this.emitTimeout)
    }
  },
  methods: {
    addIdsToNodes(nodes: RubriqueNode[], parentPath = ''): RubriqueNode[] {
      return nodes.map((node, index) => {
        const nodePath = parentPath ? `${parentPath}-${index}` : `${index}`
        const newNode = { ...node, id: `node-${nodePath}` }
        if (node.children && node.children.length > 0) {
          newNode.children = this.addIdsToNodes(node.children, nodePath)
        }
        return newNode
      })
    },
    toggleDebugJson() {
      this.showDebugJson = !this.showDebugJson
    },
    addRootNode() {
      this.editForm = {
        label: '',
        value: '',
        interencheres: '',
        drouot: '',
        policeBook: '',
        weaponBook: '',
        vehicule: '',
        TVA: '',
      }
      this.currentNode = null
      this.currentData = null
      this.isAddingChild = false
      this.editDialogVisible = true
    },
    addChild(node: TreeNode, data: RubriqueNode) {
      this.editForm = {
        label: '',
        value: '',
        interencheres: '',
        drouot: '',
        policeBook: '',
        weaponBook: '',
        vehicule: '',
        TVA: '',
      }
      this.currentNode = node
      this.currentData = data
      this.isAddingChild = true
      this.editDialogVisible = true
    },
    editNode(node: TreeNode, data: RubriqueNode) {
      this.editForm = {
        label: data.label || '',
        value: data.value || '',
        interencheres: this.showExtraFields ? data.interencheres || '' : '',
        drouot: this.showExtraFields ? data.drouot || '' : '',
        policeBook: this.showExtraFields ? data.policeBook || '' : '',
        weaponBook: this.showExtraFields ? data.weaponBook || '' : '',
        vehicule: this.showExtraFields ? data.vehicule || '' : '',
        TVA: this.showExtraFields ? data.TVA || '' : '',
      }
      this.currentNode = node
      this.currentData = data
      this.isAddingChild = false
      this.editDialogVisible = true
    },
    deleteNode(node: TreeNode, data: RubriqueNode) {
      this.$confirm('Êtes-vous sûr de vouloir supprimer cette rubrique ?', 'Confirmation', {
        confirmButtonText: 'Oui',
        cancelButtonText: 'Non',
        type: 'warning',
      })
        .then(() => {
          const parent = node.parent
          // Pour les nœuds racines, parent.data est le tableau lui-même (pas un objet avec .children)
          const parentData = parent?.data
          const children: RubriqueNode[] = ((parentData?.children || parentData) ?? this.treeData) as RubriqueNode[]
          if (children) {
            const index = children.findIndex((d: RubriqueNode) => d.id === data.id)
            if (index > -1) {
              children.splice(index, 1)
            }
          }
          this.$message.success('Rubrique supprimée')
          // Set flag before emitting to prevent redraw
          this.skipNextPropUpdate = true
          this.$nextTick(() => {
            this.emitUpdate()
          })
        })
        .catch(() => {
          // User cancelled the deletion
        })
    },
    saveNode() {
      if (!this.editForm.label) {
        this.$message.error('Le libellé est obligatoire')
        return
      }
      if (!this.editForm.value) {
        this.$message.error('La valeur (code) est obligatoire')
        return
      }
      if (!/^[a-zA-Z0-9]+$/.test(this.editForm.value)) {
        this.$message.error('La valeur (code) doit contenir uniquement des caractères alphanumériques (sans espaces ni caractères spéciaux)')
        return
      }
      const excludeId = !this.isAddingChild && this.currentData ? this.currentData.id ?? null : null
      const existingValues = this.collectAllValues(this.treeData, excludeId).map((v) => v.toLowerCase())
      if (existingValues.includes(this.editForm.value.toLowerCase())) {
        this.$message.error('Ce code existe déjà pour une autre rubrique')
        return
      }

      const newNodeData: RubriqueNode = {
        label: this.editForm.label,
        value: this.editForm.value,
      }

      // Ajouter les champs optionnels seulement s'ils sont visibles et non vides
      if (this.showExtraFields) {
        if (this.editForm.interencheres) newNodeData.interencheres = this.editForm.interencheres
        if (this.editForm.drouot) newNodeData.drouot = this.editForm.drouot
        if (this.editForm.policeBook) newNodeData.policeBook = this.editForm.policeBook
        if (this.editForm.weaponBook) newNodeData.weaponBook = this.editForm.weaponBook
        if (this.editForm.vehicule) newNodeData.vehicule = this.editForm.vehicule
        if (this.editForm.TVA) newNodeData.TVA = this.editForm.TVA
      }

      if (this.isAddingChild && this.currentNode && this.currentData) {
        // Ajout d'un enfant
        if (!this.currentData.children) {
          this.$set(this.currentData, 'children', [])
        }
        const children = this.currentData.children as RubriqueNode[]
        children.push({ ...newNodeData, id: `node-temp-${Date.now()}-${Math.random()}` })
      } else if (this.currentData && this.currentNode) {
        // Édition d'un nœud existant
        Object.assign(this.currentData, newNodeData)
      } else {
        // Ajout d'un nœud racine
        this.treeData.push({ ...newNodeData, id: `node-temp-${Date.now()}-${Math.random()}` })
      }

      this.editDialogVisible = false
      this.$message.success('Rubrique enregistrée')
      // Set flag before emitting to prevent redraw
      this.skipNextPropUpdate = true
      this.$nextTick(() => {
        this.emitUpdate()
      })
    },
    handleNodeDrop() {
      this.$message.success('Ordre modifié')
      this.skipNextPropUpdate = true
      this.$nextTick(() => {
        this.emitUpdate()
      })
    },
    expandAll(): void {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tree = this.$refs.tree as any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nodes = tree.store._getAllNodes() as TreeNode[]
      nodes.forEach((node: TreeNode) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (node as any).expanded = true;
      })
    },
    collapseAll(): void {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tree = this.$refs.tree as any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nodes = tree.store._getAllNodes() as TreeNode[]
      nodes.forEach((node: TreeNode) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (node as any).expanded = false;
      })
    },
    emitUpdate() {
      // Supprimer les IDs avant d'émettre
      const cleanData = this.removeIds(JSON.parse(JSON.stringify(this.treeData)))
      this.$emit('input', cleanData)
    },
    collectAllValues(nodes: RubriqueNode[], excludeId: string | null = null): string[] {
      let values: string[] = []
      for (const node of nodes) {
        if (node.id !== excludeId && node.value) {
          values.push(node.value)
        }
        if (node.children && node.children.length > 0) {
          values = values.concat(this.collectAllValues(node.children, excludeId))
        }
      }
      return values
    },
    removeIds(nodes: RubriqueNode[]): RubriqueNode[] {
      return nodes.map((node) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: _id, ...rest } = node
        if (rest.children && rest.children.length > 0) {
          rest.children = this.removeIds(rest.children)
        }
        return rest
      })
    },
  },
})
